// Emisor de la sesión viva de caja (PLAN-ESPEJO-POS-Y-PROPINA, F2). El POS 2.0
// escribe aquí lo que la pantalla espejo (/display de krispis-web) pinta. Solo
// existe en el admin: la web únicamente LEE la sesión (y escribe sus dos
// bloques, tip y customerRequest, desde la propia pantalla).
//
// Dos reglas de este módulo:
// - TODO es fire-and-forget con catch silencioso: la venta NUNCA se bloquea ni
//   se ensucia con errores del espejo (misma filosofía que el commit de
//   inventario en saveOrder).
// - Las escrituras van con merge y SOLO sobre los campos que el POS posee en
//   ese momento: `tip` y `customerRequest` son del espejo y una escritura del
//   carrito no debe pisarlos (se limpian explícitamente al cerrar la venta o
//   la sesión). Con merge, un campo omitido conserva su valor anterior — por
//   eso el payload del carrito escribe TODOS sus campos (null en vez de
//   ausente).
//
// El debounce y el id de la última sesión viven a nivel módulo a propósito:
// solo hay UN POS montado por app (mismo supuesto que el carrito en useState).
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import {
    PosSession,
    PosSessionCart,
    PosSessionLastOrder,
    PosSessionStage,
    PosSessionTotals,
    getPosSessionId,
    posSessionsRef
} from '@api/PosSessions/PosSession';

const SYNC_DEBOUNCE_MS = 400;

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSessionId: string | undefined;
// El payload debounced que aún NO se ha escrito (se guarda además del timer
// para poder inspeccionarlo/descartarlo con precisión).
let pendingPayload: Record<string, unknown> | undefined;

export interface PosSessionIdentity {
    branchOfficeId: string;
    collaboratorId: string;
    collaboratorName: string;
}

/**
 * Escribe el espejo. **Nunca lanza y nunca se espera**: la llamada va con `void`
 * a propósito (el linter lo exige desde el 2026-09-11 y está bien que lo exija).
 * La sesión es un espejo, no un dato de negocio — si la escritura tarda, el
 * cajero no puede quedarse esperando; y como los errores se atienden aquí
 * dentro, un `void` aquí no esconde nada.
 */
const writeSession = async (sessionId: string, payload: Record<string, unknown>) => {
    try {
        await setDoc(doc(posSessionsRef, sessionId), { ...payload, updatedAt: new Date() }, { merge: true });
    } catch (error) {
        // La sesión es un espejo, no un dato de negocio: si falla, la venta sigue.
        console.warn('No se pudo escribir la sesión del POS (el espejo puede quedarse atrás)', error);
    }
};

const cancelPendingSync = () => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = undefined;
    }
    pendingPayload = undefined;
};


const PosSessionServ = {
    // Carrito → sesión, con debounce (una escritura por ráfaga de cambios).
    syncCart(identity: PosSessionIdentity, cart: PosSessionCart, totals: PosSessionTotals): void {
        const sessionId = getPosSessionId(identity.branchOfficeId, identity.collaboratorId);
        // Cambio de sucursal (o de usuario) con la pantalla abierta: la sesión
        // anterior se limpia para que su espejo no pinte un carrito muerto.
        if (lastSessionId && lastSessionId !== sessionId) {
            cancelPendingSync();
            void writeSession(lastSessionId, {
                stage: 'idle',
                cart: null,
                totals: null,
                tip: null,
                customerRequest: null,
                identifyRequestedAt: null
            });
        }
        lastSessionId = sessionId;
        const hasLines = cart.products.length > 0;
        const stage: PosSessionStage = hasLines ? 'building' : 'idle';
        cancelPendingSync();
        pendingPayload = {
            branchOfficeId: identity.branchOfficeId,
            collaboratorId: identity.collaboratorId,
            collaboratorName: identity.collaboratorName,
            stage,
            cart: hasLines
                ? {
                      products: cart.products,
                      customerName: cart.customerName || null,
                      customerHasAccount: cart.customerHasAccount || false,
                      note: cart.note || null
                  }
                : null,
            totals: hasLines ? totals : null,
            // Sin líneas es un pedido NUEVO: la propina (y la confirmación) del
            // anterior no puede sobrevivir, o el POS la re-aplicaría sola.
            ...(hasLines ? {} : { tip: null }),
            appVersion: process.env.REACT_APP_VERSION || null
        };
        debounceTimer = setTimeout(() => {
            debounceTimer = undefined;
            const payload = pendingPayload;
            pendingPayload = undefined;
            if (payload) void writeSession(sessionId, payload);
        }, SYNC_DEBOUNCE_MS);
    },

    // Venta cerrada: el espejo muestra el folio ("¡gracias!" o "pendiente de
    // pago" si quedó WaitingForPayment). Inmediato, sin debounce — y cancela
    // cualquier sync pendiente del carrito que ya quedó atrás.
    registerLastOrder(identity: PosSessionIdentity, lastOrder: PosSessionLastOrder): void {
        cancelPendingSync();
        const sessionId = getPosSessionId(identity.branchOfficeId, identity.collaboratorId);
        lastSessionId = sessionId;
        void writeSession(sessionId, {
            branchOfficeId: identity.branchOfficeId,
            collaboratorId: identity.collaboratorId,
            collaboratorName: identity.collaboratorName,
            stage: 'done',
            cart: null,
            totals: null,
            tip: null,
            customerRequest: null,
            identifyRequestedAt: null,
            lastOrder
        });
    },

    // Al salir del POS (unmount): el carrito local muere con la pantalla, así
    // que la sesión no debe quedarse pintando un pedido que ya no existe.
    // `lastOrder` se conserva — el espejo sigue mostrando la última venta.
    clearSession(): void {
        cancelPendingSync();
        if (!lastSessionId) return;
        void writeSession(lastSessionId, {
            stage: 'idle',
            cart: null,
            totals: null,
            tip: null,
            customerRequest: null,
            identifyRequestedAt: null
        });
        lastSessionId = undefined;
    },

    // D7: el POS consumió (o descartó) la identificación del espejo.
    clearCustomerRequest(identity: PosSessionIdentity): void {
        void writeSession(getPosSessionId(identity.branchOfficeId, identity.collaboratorId), { customerRequest: null });
    },

    // Ajuste QA 2026-08-26: el cajero manda la identificación A LA PANTALLA del
    // cliente — el espejo abre su teclado de teléfono al ver CAMBIAR el valor.
    requestIdentify(identity: PosSessionIdentity): void {
        void writeSession(getPosSessionId(identity.branchOfficeId, identity.collaboratorId), {
            identifyRequestedAt: new Date()
        });
    },

    // El cajero quitó la propina con el bote de basura del resumen: se limpia el
    // bloque para que el espejo le devuelva el selector al cliente. Es la ÚNICA
    // escritura del POS sobre `tip` — elegirla es del cliente (rediseño
    // 2026-08-26, 4ª ronda).
    clearTip(identity: PosSessionIdentity): void {
        void writeSession(getPosSessionId(identity.branchOfficeId, identity.collaboratorId), { tip: null });
    },

    // Suscripción del propio POS a su sesión — por aquí le llegan los bloques
    // que escribe el espejo: `customerRequest` (F4) y `tip` (F5).
    getPosSessionRealtime(sessionId: string, callback: (session?: PosSession) => void): () => void {
        return onSnapshot(doc(posSessionsRef, sessionId), (snapshot) => {
            const data = snapshot.data() as PosSession | undefined;
            if (data) data.id = snapshot.id;
            callback(data);
        });
    }
};

export default PosSessionServ;
